import { signal } from "../net/signal";

/**
 * فرشِ میز — انتخابِ بازیکن.
 *
 * پیش‌فرض همان فرشِ اپ است تا وب و موبایل از اول یکی باشند؛ چهار فرشِ دیگر از
 * `docs/carpet-prompts.md` آمده‌اند (دو تای دیگرِ آن دور رد شدند: یکی شبکه‌ی
 * پرکنتراستِ کاشی زیرِ کارت، یکی فرشِ روشن که ورقِ عاجی رویش گم می‌شد).
 *
 * انتخاب سلیقه‌ی این دستگاه است، نه چیزی که سرور بداند، پس در localStorage
 * می‌مانَد — و اگر localStorage نبود یا خطا داد، فقط پیش‌فرض می‌مانَد.
 */
export type CarpetKey = "eyvan" | "lajvard" | "mahoot" | "gelim" | "shab";

/**
 * `bg` و `round`: هر پنج فرش قاب دارند — گردِ ایوان حاشیه‌ی طلایی دورِ خودش، و
 * چهار تای دیگر قابِ چهارگوش. روی صفحه‌ی بلندِ گوشی `cover` تصویر را تا ارتفاع
 * بزرگ می‌کرد و از دو پهلو می‌بُرید، پس حاشیه‌ی چپ و راست کلاً بیرون می‌افتاد.
 * حالا فرش کاملْ داخلِ کادر می‌نشیند و دورِ آن `bg` می‌آید — رنگِ خودِ لبه‌ی
 * همان فرش، کمی تیره‌تر، تا مرزِ فرش و زمینه درز نیندازد.
 *
 * `round` فقط برای سایه است: سایه‌ی دایره‌ایْ زیرِ فرشِ چهارگوش بی‌ربط می‌شد.
 */
export const carpets: { key: CarpetKey; fa: string; file: string; bg: string; round?: true }[] = [
  { key: "eyvan", fa: "ترنجِ ایوان", file: "carpet.webp", bg: "#202249", round: true },
  { key: "lajvard", fa: "ترنجِ لاجورد", file: "carpet-lajvard.webp", bg: "#1e1e42" },
  { key: "mahoot", fa: "ماهوتِ سبز", file: "carpet-mahoot.webp", bg: "#274838" },
  { key: "gelim", fa: "گلیمِ سرخ", file: "carpet-gelim.webp", bg: "#732e2d" },
  { key: "shab", fa: "شبِ نقره‌ای", file: "carpet-shab.webp", bg: "#0a0a14" },
];

const KEY = "bazivan.carpet";

function load(): CarpetKey {
  try {
    const v = localStorage.getItem(KEY);
    if (v && carpets.some((c) => c.key === v)) return v as CarpetKey;
  } catch { /* حالتِ ناشناس */ }
  return "eyvan";
}

export const carpet = signal<CarpetKey>(load());

export function thumbOf(key: CarpetKey): string {
  return key === "eyvan" ? "/art/table/carpet-thumb.webp" : `/art/table/carpet-${key}-thumb.webp`;
}

/** سلیقه‌ی محلی: فرشِ پیش‌فرضِ اتاقی که خودم می‌سازم. */
export function setCarpet(key: CarpetKey): void {
  carpet.value = key;
  try { localStorage.setItem(KEY, key); } catch { /* بی‌ذخیره هم کار می‌کند */ }
}

export function isCarpet(k: string): k is CarpetKey { return carpets.some((c) => c.key === k); }

/**
 * فرشِ میزی که سرِ آنیم — از **وضعیتِ سرور**، نه از سلیقه‌ی این دستگاه.
 *
 * فرش مالِ اتاق است: میزبان انتخاب می‌کند و همه — وب و موبایل — همان را
 * می‌بینند. اگر سرور کلیدِ خالی داد (میزِ کهنه)، پیش‌فرض.
 */
export function showCarpet(key: string | undefined): void {
  const c = carpets.find((x) => x.key === key) ?? carpets[0]!;
  const css = document.documentElement.style;
  css.setProperty("--carpet-url", `url("/art/table/${c.file}")`);
  css.setProperty("--carpet-bg", c.bg);
  css.setProperty("--carpet-shadow", c.round
    ? "radial-gradient(circle closest-side at 50% 50%, #00000066 0 100%, #00000038 107%, #00000000 116%)"
    : "none");
}

if (typeof document !== "undefined") showCarpet("eyvan");
